import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { datadogLogs } from '@datadog/browser-logs';
import { AuthContext, AuthContextType } from './AuthContext';
import { useApiToken } from './useApiToken';

export const OktaAuthProvider = ({ children }: { children: ReactNode }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const { token, installToken, dropToken } = useApiToken('okta');
  useEffect(() => {
    let isMounted = true;
    // One renewal fires added/renewed/removed, so reads overlap; without this an older one clears a newer token.
    let latestRead = 0;

    const getToken = async () => {
      const read = ++latestRead;
      try {
        const tokenResponse = await oktaAuth.tokenManager.get('accessToken');
        // get() resolves undefined rather than throwing once a failed renewal removed the token.
        const accessToken = tokenResponse && 'accessToken' in tokenResponse ? tokenResponse.accessToken : null;

        if (!isMounted || read !== latestRead) return;

        if (accessToken) {
          installToken(accessToken);
        } else {
          dropToken();
        }
      } catch (error) {
        datadogLogs.logger.error('Error fetching token', { error });
        if (isMounted && read === latestRead) dropToken();
      }
    };

    if (authState?.isAuthenticated) {
      getToken();
    } else {
      // Sign-out and session-loss land here, so the bearer has to go too, not just the React copy.
      dropToken();
    }

    const handleTokenUpdate = () => getToken();

    oktaAuth?.tokenManager?.on('added', handleTokenUpdate);
    oktaAuth?.tokenManager?.on('renewed', handleTokenUpdate);
    oktaAuth?.tokenManager?.on('removed', handleTokenUpdate);

    return () => {
      isMounted = false;
      oktaAuth?.tokenManager?.off('added', handleTokenUpdate);
      oktaAuth?.tokenManager?.off('renewed', handleTokenUpdate);
      oktaAuth?.tokenManager?.off('removed', handleTokenUpdate);
    };
  }, [authState, oktaAuth, installToken, dropToken]);

  const signIn = useCallback(
    async (returnTo: string) => {
      oktaAuth.setOriginalUri(returnTo);
      await oktaAuth.signInWithRedirect();
    },
    [oktaAuth]
  );

  const signOut = useCallback(async () => {
    await oktaAuth.signOut();
  }, [oktaAuth]);

  const value = useMemo<AuthContextType>(
    () => ({
      provider: 'okta',
      authState: authState
        ? { isAuthenticated: !!authState.isAuthenticated, isPending: authState.isPending === true }
        : null,
      token,
      signIn,
      signOut
    }),
    [authState, token, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
